import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";

/** Один слот в дне расписания квеста. */
export interface QSSlot {
  time: string;
  status: "free" | "booked" | "paid" | "past";
  price: number;
  client: string | null;
}

/** День расписания (дата + день недели + слоты). */
export interface QSDay {
  date: string; // YYYY-MM-DD
  dow: number; // 0..6 (вс..сб)
  slots: QSSlot[];
}

export interface QuestScheduleResponse {
  quest: { id: string; name: string; short_name: string };
  days: QSDay[];
}

export interface QuestScheduleResult {
  quest: QuestScheduleResponse["quest"] | null;
  days: QSDay[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Многодневное расписание одного квеста (для DC-вида «расписание квеста»).
 * Тянет /api/quest-schedule/{id}?days=N. Default 30 дней вперёд.
 */
export function useQuestSchedule(questId: string | null, days = 30): QuestScheduleResult {
  const [quest, setQuest] = useState<QuestScheduleResponse["quest"] | null>(null);
  const [daysData, setDaysData] = useState<QSDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!questId) {
      setQuest(null);
      setDaysData([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<QuestScheduleResponse>(`/api/quest-schedule/${encodeURIComponent(questId)}?days=${days}`)
      .then((res) => {
        if (cancelled) return;
        setQuest(res.quest ?? null);
        setDaysData(Array.isArray(res.days) ? res.days : []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError((e as Error)?.message || "Не удалось загрузить расписание");
        setQuest(null);
        setDaysData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [questId, days, tick]);

  return { quest, days: daysData, loading, error, reload };
}
